<template>
    <div class="guide-availability">
        <div class="level guide-availability__toolbar">
            <div class="level-left">
                <div class="level-item guide-availability__nav">
                    <button class="button is-small" type="button" title="Zurück" v-on:click="step(-1)"><i class="material-icons">chevron_left</i></button>
                    <h2 class="mb-0">{{ title }}</h2>
                    <button class="button is-small" type="button" title="Weiter" v-on:click="step(1)"><i class="material-icons">chevron_right</i></button>
                    <button class="button is-small" type="button" v-if="month !== null" v-on:click="month = null">Ganzes Jahr</button>
                </div>
            </div>
            <div class="level-right">
                <div class="level-item guide-availability__legend">
                    <span v-for="(label, key) in labels" v-bind:key="key"><i class="swatch" v-bind:class="'is-' + key"></i>{{ label }}</span>
                    <span><i class="swatch is-none"></i>keine Angabe</span>
                    <span><i class="swatch is-group"></i>unsere Gruppe</span>
                </div>
            </div>
        </div>

        <loading v-if="isLoading && !loadedYear" />

        <div v-else-if="month === null" class="guide-availability__year" v-on:pointerdown="onPointerDown">
            <div class="guide-availability__month" v-for="(name, m) in monthNames" v-bind:key="m">
                <h3><button type="button" v-on:click="month = m" v-bind:title="name + ' öffnen'">{{ name }}</button></h3>
                <div class="guide-availability__grid">
                    <div class="ga-weekday" v-for="wd in weekdays" v-bind:key="wd">{{ wd[0] }}</div>
                    <div class="ga-day is-blank" v-for="n in leadingBlanks(m)" v-bind:key="'b' + n"></div>
                    <div
                        v-for="day in daysOf(m)"
                        v-bind:key="day.key"
                        class="ga-day"
                        v-bind:class="dayClasses(day)"
                        v-bind:data-key="day.key"
                        v-bind:title="dayTitle(day)"
                    >{{ day.d }}</div>
                </div>
            </div>
        </div>

        <div v-else class="guide-availability__zoom" v-on:pointerdown="onPointerDown">
            <div class="guide-availability__grid">
                <div class="ga-weekday" v-for="wd in weekdays" v-bind:key="wd">{{ wd }}</div>
                <div class="ga-day is-blank" v-for="n in leadingBlanks(month)" v-bind:key="'b' + n"></div>
                <div
                    v-for="day in daysOf(month)"
                    v-bind:key="day.key"
                    class="ga-day"
                    v-bind:class="dayClasses(day)"
                    v-bind:data-key="day.key"
                    v-bind:title="dayTitle(day)"
                >
                    <span class="ga-day__number">{{ day.d }}</span>
                    <span class="ga-day__note" v-if="periodByDay[day.key] && periodByDay[day.key].note">{{ periodByDay[day.key].note }}</span>
                    <a
                        v-for="group in groupsByDay[day.key] || []"
                        v-bind:key="group.orderId"
                        class="ga-day__group"
                        v-bind:href="'/orders/' + group.orderId"
                        target="_blank"
                        v-on:pointerdown.stop
                    >{{ group.orderId }} {{ group.name }}</a>
                </div>
            </div>
        </div>

        <div class="guide-availability__entry" v-if="selection">
            <strong>{{ selectionLabel }}</strong>
            <button
                v-for="(label, key, index) in labels"
                v-bind:key="key"
                type="button"
                class="button is-status"
                v-bind:class="'is-' + key"
                v-bind:disabled="isSaving"
                v-on:click="save(key)"
            >{{ label }} <kbd>{{ index + 1 }}</kbd></button>
            <button type="button" class="button" v-bind:disabled="isSaving" v-on:click="save(null)">löschen</button>
            <input
                class="input"
                type="text"
                ref="note"
                v-model="note"
                maxlength="255"
                placeholder="Notiz, optional (z. B. „Optionen bei anderen“)"
                v-on:keydown.enter="$event.target.blur()"
            >
            <button type="button" class="button is-text" title="Auswahl aufheben (Esc)" v-on:click="clearSelection"><i class="material-icons">close</i></button>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import Mixin from '../tab';
import Loading from '@components/Loading.vue';
import { getAxios, axiosByMethod } from '@api';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export default {
    mixins: [ Mixin ],

    components: { Loading },

    data: function () {
        return {
            year: moment().month() >= 9 ? moment().year() + 1 : moment().year(),
            month: null,
            loadedYear: null,
            periods: [],
            groups: [],
            isLoading: false,
            isSaving: false,

            anchor: null,
            cursor: null,
            dragging: false,
            note: '',

            labels: { free: 'frei', maybe: 'unsicher', full: 'belegt' },
            monthNames: MONTHS,
            weekdays: WEEKDAYS,
        }
    },

    computed: {
        title: function () {
            return this.month === null ? String(this.year) : `${MONTHS[this.month]} ${this.year}`;
        },

        periodByDay: function () {
            const map = {};
            this.periods.forEach(period => {
                eachDay(period.from, period.to, key => { map[key] = period; });
            });
            return map;
        },

        groupsByDay: function () {
            const map = {};
            this.groups.forEach(group => {
                eachDay(group.from, group.to, key => { (map[key] = map[key] || []).push(group); });
            });
            return map;
        },

        selection: function () {
            if (!this.anchor) {
                return null;
            }
            const end = this.cursor || this.anchor;
            return this.anchor <= end ? [this.anchor, end] : [end, this.anchor];
        },

        selectionLabel: function () {
            const [from, to] = this.selection;
            const days = moment(to).diff(moment(from), 'days') + 1;
            const range = from === to
                ? moment(from).format('DD.MM.YYYY')
                : `${moment(from).format('DD.MM.')}–${moment(to).format('DD.MM.YYYY')}`;
            return `${range} · ${days} ${days === 1 ? 'Tag' : 'Tage'}`;
        },
    },

    methods: {
        load: function () {
            if (!this.item || !this.item.id) {
                return;
            }
            this.isLoading = true;
            const year = this.year;
            return getAxios(`guide_availability/${this.item.id}`, { from: `${year}-01-01`, to: `${year}-12-31` })
                .then(response => {
                    if (year !== this.year) {
                        return;
                    }
                    this.periods = response.data.periods;
                    this.groups = response.data.groups;
                    this.loadedYear = year;
                })
                .catch(() => this.notifyError('Verfügbarkeit konnte nicht geladen werden'))
                .finally(() => { this.isLoading = false; });
        },

        save: function (status) {
            if (!this.selection || this.isSaving) {
                return;
            }
            const [from, to] = this.selection;
            this.isSaving = true;
            axiosByMethod('post', `guide_availability/${this.item.id}`, { from, to, status, note: status ? this.note : null })
                .then(() => {
                    this.clearSelection();
                    return this.load();
                })
                .catch(() => this.notifyError('Speichern hat nicht geklappt'))
                .finally(() => { this.isSaving = false; });
        },

        step: function (direction) {
            this.clearSelection();
            if (this.month === null) {
                this.year += direction;
                return;
            }
            const next = this.month + direction;
            if (next < 0 || next > 11) {
                this.year += direction;
                this.month = next < 0 ? 11 : 0;
            } else {
                this.month = next;
            }
        },

        daysOf: function (m) {
            const count = moment({ year: this.year, month: m }).daysInMonth();
            const days = [];
            for (let d = 1; d <= count; d++) {
                const date = moment({ year: this.year, month: m, day: d });
                days.push({ d, key: date.format('YYYY-MM-DD'), weekend: date.isoWeekday() >= 6 });
            }
            return days;
        },

        leadingBlanks: function (m) {
            return moment({ year: this.year, month: m, day: 1 }).isoWeekday() - 1;
        },

        dayClasses: function (day) {
            const period = this.periodByDay[day.key];
            return {
                ['is-' + (period ? period.status : 'none')]: true,
                'is-weekend': day.weekend,
                'is-group': !!this.groupsByDay[day.key],
                'is-selected': !!this.selection && day.key >= this.selection[0] && day.key <= this.selection[1],
            };
        },

        dayTitle: function (day) {
            const period = this.periodByDay[day.key];
            const parts = [moment(day.key).format('dd, DD.MM.YYYY')];
            parts.push(period ? this.labels[period.status] + (period.note ? ` – ${period.note}` : '') : 'keine Angabe');
            if (period && period.updatedBy) {
                parts.push(`eingetragen von ${period.updatedBy}, ${moment(period.updatedAt).format('DD.MM.YYYY')}`);
            }
            (this.groupsByDay[day.key] || []).forEach(group => parts.push(`Gruppe ${group.orderId} ${group.name || ''} (${group.client || ''})`));
            return parts.join('\n');
        },

        // Selecting days: drag across days, or click and Shift+click
        dayKeyAt: function (event) {
            const element = document.elementFromPoint(event.clientX, event.clientY);
            const day = element && element.closest('.guide-availability .ga-day[data-key]');
            return day ? day.dataset.key : null;
        },

        onPointerDown: function (event) {
            const key = this.dayKeyAt(event);
            if (!key) {
                return;
            }
            event.preventDefault();
            if (event.shiftKey && this.anchor) {
                this.cursor = key;
            } else {
                this.anchor = key;
                this.cursor = key;
                this.dragging = true;
            }
        },

        onPointerMove: function (event) {
            if (!this.dragging) {
                return;
            }
            const key = this.dayKeyAt(event);
            if (key && key !== this.cursor) {
                this.cursor = key;
            }
        },

        onPointerUp: function () {
            this.dragging = false;
        },

        onKeyDown: function (event) {
            if (!this.selection || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName) || event.target.isContentEditable) {
                return;
            }
            const status = { '1': 'free', '2': 'maybe', '3': 'full' }[event.key];
            if (status) {
                this.save(status);
            } else if (event.key === 'Delete' || event.key === 'Backspace') {
                this.save(null);
            } else if (event.key === 'Escape') {
                this.clearSelection();
            } else {
                return;
            }
            event.preventDefault();
        },

        clearSelection: function () {
            this.anchor = null;
            this.cursor = null;
            this.note = '';
        },

        listen: function (on) {
            const method = on ? 'addEventListener' : 'removeEventListener';
            window[method]('pointermove', this.onPointerMove);
            window[method]('pointerup', this.onPointerUp);
            document[method]('keydown', this.onKeyDown);
        },
    },

    watch: {
        year: function () { this.load(); },
        'item.id': function () { this.load(); },
    },

    mounted: function () {
        this.load();
    },

    // The tab lives in keep-alive: only listen while it is shown
    activated: function () { this.listen(true); },
    deactivated: function () { this.listen(false); this.dragging = false; },
    beforeDestroy: function () { this.listen(false); },
}

function eachDay (from, to, callback) {
    const day = moment(from);
    const end = moment(to);
    while (day.isSameOrBefore(end, 'day')) {
        callback(day.format('YYYY-MM-DD'));
        day.add(1, 'day');
    }
}
</script>
